import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, Tag, Space, Typography, Drawer
} from 'antd';
import { ArrowLeftOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const TASK_TYPES = {
  unbind: { text: '设备解绑', color: 'blue' },
  limitVendor: { text: '限定卡商', color: 'orange' },
  cancelVendor: { text: '取消限定', color: 'orange' },
  disable: { text: '设备禁用', color: 'red' },
  enable: { text: '解除禁用', color: 'red' },
  reset: { text: '设备置新', color: 'green' },
  flowReset: { text: '流量置空', color: 'purple' },
};

const PROCESS_STATUS = {
  done: { text: '已完成', color: 'success' },
  processing: { text: '处理中', color: 'processing' },
  fail: { text: '失败', color: 'error' },
  pending: { text: '待处理', color: 'default' },
};

const MOCK_TASKS = {
  '1': { taskType: 'unbind', createdAt: '2026-04-10 14:30:00', success: 48, failed: 2, total: 50 },
  '2': { taskType: 'limitVendor', createdAt: '2026-04-02 17:10:53', success: 1, failed: 0, total: 1 },
  '3': { taskType: 'disable', createdAt: '2026-04-08 16:45:00', success: 24, failed: 1, total: 30 },
  '4': { taskType: 'reset', createdAt: '2026-04-07 09:00:00', success: 195, failed: 5, total: 200 },
  '5': { taskType: 'limitVendor', createdAt: '2026-04-06 11:20:00', success: 4, failed: 0, total: 10 },
};

const MOCK_DEVICES = [
  { key: '1', license: 'CM0923MRO89HX', status: 'done', attempts: 2, failCount: 0, updatedAt: '2026-04-02 17:10:55' },
  { key: '2', license: 'CM0923MRO89HY', status: 'done', attempts: 1, failCount: 0, updatedAt: '2026-04-02 17:11:02' },
  { key: '3', license: 'CM0923MRO89HZ', status: 'fail', attempts: 3, failCount: 3, updatedAt: '2026-04-02 17:12:30' },
  { key: '4', license: 'CM0923MRO89HA', status: 'processing', attempts: 1, failCount: 0, updatedAt: '2026-04-02 17:13:00' },
  { key: '5', license: 'CM0923MRO89HB', status: 'pending', attempts: 0, failCount: 0, updatedAt: '——' },
];

const PRODUCTS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1' },
  { value: 'PROD_P1', label: '云台机P1' },
  { value: 'PROD_F1', label: '烟雾报警器F1' },
];

const MaintenanceDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('id') || '2';
  const task = MOCK_TASKS[taskId] || MOCK_TASKS['2'];
  const taskInfo = TASK_TYPES[task.taskType];
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);

  const columns = [
    { title: '序号', width: 60, align: 'center', render: (_, __, i) => i + 1 },
    { title: 'License', dataIndex: 'license', width: 200 },
    {
      title: '最新状态', dataIndex: 'status', width: 120,
      render: (v) => { const s = PROCESS_STATUS[v] || { text: v, color: 'default' }; return <Tag color={s.color}>{s.text}</Tag>; },
    },
    { title: '处理次数', dataIndex: 'attempts', width: 100 },
    {
      title: '失败次数', dataIndex: 'failCount', width: 100,
      render: (v) => v > 0 ? <Text type="danger">{v}</Text> : 0,
    },
    { title: '更新时间(UTC)', dataIndex: 'updatedAt', width: 200, sorter: (a, b) => a.updatedAt.localeCompare(b.updatedAt), showSorterTooltip: { title: '点击排序' } },
    {
      title: '操作', key: 'action', width: 100,
      render: (_, r) => <Button type="link" size="small" onClick={() => { setDrawerRecord(r); setDrawerVisible(true); }}>查看详情</Button>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/device/maintenance')} />
        <Title level={4} style={{ margin: 0 }}>运维明细</Title>
      </div>

      {/* 查询条件 */}
      <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12 }} bodyStyle={{ padding: '12px 0' }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="searchType" initialValue="license" noStyle>
                <Select style={{ width: 110 }}>
                  <Option value="license">License</Option>
                  <Option value="did">设备DID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="searchKeyword" noStyle>
                <Input placeholder="License、设备DID" allowClear style={{ width: 200 }} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="prodType" initialValue="name" noStyle>
                <Select style={{ width: 100 }}>
                  <Option value="name">产品名称</Option>
                  <Option value="id">产品ID</Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(p, c) => p.prodType !== c.prodType} noStyle>
                {({ getFieldValue }) => getFieldValue('prodType') === 'id'
                  ? <Form.Item name="prodKeyword" noStyle><Input placeholder="产品ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="prodKeyword" noStyle><Select placeholder="请选择产品" allowClear showSearch optionFilterProp="label" style={{ width: 200 }} options={PRODUCTS} /></Form.Item>
                }
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="processStatus" style={{ marginRight: 8 }}>
            <Select placeholder="处理进度" allowClear style={{ width: 130 }}>
              {Object.entries(PROCESS_STATUS).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>搜索</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 任务摘要 */}
      <Card bordered={false} style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space size={24}>
            <span>任务类型 <Tag color={taskInfo.color}>{taskInfo.text}</Tag></span>
            <span>创建时间 <Text style={{ marginLeft: 8 }}>{task.createdAt}</Text></span>
          </Space>
          <span>
            成功/失败/总设备{' '}
            <Text strong style={{ color: '#1677ff', fontSize: 16, margin: '0 2px', padding: '2px 8px', background: '#e6f4ff', borderRadius: 4 }}>{task.success}</Text>
            {' / '}
            <Text strong style={{ color: '#ff4d4f', fontSize: 16, margin: '0 2px', padding: '2px 8px', background: '#fff1f0', borderRadius: 4 }}>{task.failed}</Text>
            {' / '}
            <Text style={{ fontSize: 16, margin: '0 2px', padding: '2px 8px', background: '#f5f5f5', borderRadius: 4 }}>{task.total}</Text>
          </span>
        </div>
      </Card>

      {/* 设备列表 */}
      <Card bordered={false}>
        <Table columns={columns} dataSource={MOCK_DEVICES} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 查看详情抽屉 */}
      <Drawer title="设备详情" open={drawerVisible} onClose={() => setDrawerVisible(false)} width={600}>
        {drawerRecord && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Text>License：</Text><Text copyable strong style={{ color: '#1677ff' }}>{drawerRecord.license}</Text>
            </div>
            <Table size="small" rowKey="key" pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
              columns={[
                { title: '序号', width: 60, align: 'center', render: (_, __, i) => i + 1 },
                { title: '处理进度', dataIndex: 'status', width: 100, render: (v) => { const s = PROCESS_STATUS[v]; return <Tag color={s.color}>{s.text}</Tag>; } },
                { title: '返回错误信息', dataIndex: 'errorMsg', width: 200, render: (v) => v || '-' },
                { title: '触发时间', dataIndex: 'time', width: 180, sorter: (a, b) => a.time.localeCompare(b.time) },
              ]}
              dataSource={[
                { key: '1', status: 'done', errorMsg: '', time: '2026-04-14 14:52:20' },
                { key: '2', status: 'pending', errorMsg: '', time: '2026-04-14 14:51:04' },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default MaintenanceDetail;
